import React, { useEffect, useRef } from "react";
import { createChart } from "lightweight-charts";

const BASE_TIME = 1700000000; // synthetic base; real historical period stays hidden
const FORM_MS = 5000; // candle forming duration (5s clock)
const VOL_UP = "rgba(34,197,94,0.45)";
const VOL_DN = "rgba(239,68,68,0.45)";

// Synthetic but deterministic volume (real historical volume is not available in-match).
const computeVol = (c) => {
  const spread = Math.abs(c.c - c.o) + (c.h - c.l);
  const base = Math.abs(c.c) * 0.0008 + 1e-6;
  const jitter = 0.6 + ((c.i * 97) % 80) / 100; // 0.6..1.4, stable per candle
  return (spread + base) * jitter * 1000;
};

export const CandleChart = ({ candles, height = 360, markers = [], tooltips = {}, forming = null, resetKey = "" }) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const volSeriesRef = useRef(null);
  const tipRef = useRef(null);
  const tooltipsRef = useRef(tooltips);
  tooltipsRef.current = tooltips;
  const prevLenRef = useRef(-1);
  const prevKeyRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const chart = createChart(el, {
      width: el.clientWidth,
      height,
      localization: { locale: "en-US", timeFormatter: () => "" },
      layout: {
        background: { type: "solid", color: "#141414" },
        textColor: "#737373",
        fontFamily: "JetBrains Mono, monospace",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.1)" },
      timeScale: {
        borderColor: "rgba(255,255,255,0.1)",
        timeVisible: false,
        secondsVisible: false,
        tickMarkFormatter: () => "",
      },
      crosshair: {
        vertLine: { color: "rgba(234,179,8,0.4)", labelVisible: false },
        horzLine: { color: "rgba(234,179,8,0.4)", labelBackgroundColor: "#EAB308" },
      },
      handleScroll: false,
      handleScale: false,
    });
    const series = chart.addCandlestickSeries({
      upColor: "#22C55E",
      downColor: "#EF4444",
      borderUpColor: "#22C55E",
      borderDownColor: "#EF4444",
      wickUpColor: "#22C55E",
      wickDownColor: "#EF4444",
    });
    chartRef.current = chart;
    seriesRef.current = series;

    const volSeries = chart.addHistogramSeries({
      priceScaleId: "vol",
      priceFormat: { type: "volume" },
      lastValueVisible: false,
      priceLineVisible: false,
    });
    chart.priceScale("vol").applyOptions({ scaleMargins: { top: 0.84, bottom: 0 } });
    volSeriesRef.current = volSeries;

    const tip = document.createElement("div");
    tip.style.cssText =
      "position:absolute;display:none;z-index:20;pointer-events:none;background:#0A0A0A;border:1px solid rgba(234,179,8,0.5);color:#fff;font:11px/1.4 'JetBrains Mono',monospace;padding:6px 8px;border-radius:2px;max-width:220px;";
    el.style.position = "relative";
    el.appendChild(tip);
    tipRef.current = tip;

    chart.subscribeCrosshairMove((param) => {
      if (!param.point || param.time == null) {
        tip.style.display = "none";
        return;
      }
      const step = Math.round((param.time - BASE_TIME) / 5);
      const text = tooltipsRef.current[step];
      if (!text) {
        tip.style.display = "none";
        return;
      }
      tip.textContent = text;
      tip.style.display = "block";
      tip.style.left = Math.min(param.point.x + 12, el.clientWidth - 160) + "px";
      tip.style.top = Math.max(param.point.y - 10, 6) + "px";
    });

    const ro = new ResizeObserver(() => {
      if (containerRef.current) chart.applyOptions({ width: containerRef.current.clientWidth });
    });
    ro.observe(el);

    return () => {
      if (animRef.current) clearTimeout(animRef.current);
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volSeriesRef.current = null;
    };
  }, [height]);

  // set completed candles (redraw only when length or instrument changes)
  useEffect(() => {
    if (!seriesRef.current || !candles) return;
    const changed = resetKey !== prevKeyRef.current || candles.length !== prevLenRef.current;
    prevKeyRef.current = resetKey;
    prevLenRef.current = candles.length;
    if (!changed) return;
    const data = candles.map((c) => ({
      time: BASE_TIME + c.i * 5, open: c.o, high: c.h, low: c.l, close: c.c,
    }));
    seriesRef.current.setData(data);
    if (volSeriesRef.current) {
      volSeriesRef.current.setData(candles.map((c) => ({
        time: BASE_TIME + c.i * 5, value: computeVol(c),
        color: c.c >= c.o ? VOL_UP : VOL_DN,
      })));
    }
    if (chartRef.current && data.length) chartRef.current.timeScale().fitContent();
  }, [candles, resetKey]);

  // animate the currently forming candle over ~5 seconds
  const formingRef = useRef(forming);
  formingRef.current = forming;
  const formingIdx = forming ? forming.i : -1;
  useEffect(() => {
    if (animRef.current) { clearTimeout(animRef.current); animRef.current = null; }
    const target = formingRef.current;
    if (!seriesRef.current || !target) return;
    const t = BASE_TIME + target.i * 5;
    const start = performance.now();
    const tick = () => {
      if (!seriesRef.current) return;
      const f = Math.min(1, (performance.now() - start) / FORM_MS);
      const o = target.o;
      const close = o + (target.c - o) * f;
      const high = Math.max(o, close, o + (target.h - o) * f);
      const low = Math.min(o, close, o + (target.l - o) * f);
      seriesRef.current.update({ time: t, open: o, high, low, close });
      if (volSeriesRef.current) {
        volSeriesRef.current.update({
          time: t, value: computeVol({ i: target.i, o, c: close, h: high, l: low }) * (0.4 + 0.6 * f),
          color: close >= o ? VOL_UP : VOL_DN,
        });
      }
      if (f < 1) animRef.current = setTimeout(tick, 70);
    };
    tick();
    return () => { if (animRef.current) clearTimeout(animRef.current); };
  }, [formingIdx, resetKey]);

  useEffect(() => {
    if (!seriesRef.current) return;
    const m = (markers || []).map((mk) => ({
      time: BASE_TIME + mk.step * 5,
      position: mk.position || "aboveBar",
      color: mk.color || "#EAB308",
      shape: mk.shape || "circle",
      text: mk.text || "",
    }));
    m.sort((a, b) => a.time - b.time);
    seriesRef.current.setMarkers(m);
  }, [markers]);

  return <div ref={containerRef} data-testid="candle-chart" style={{ width: "100%", height }} />;
};
